import React, { useCallback, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetCustomerOverviewQuery } from "../../app/api/console/consoleCustomerAnalyticsApiSlice";
import ActionsTab from "./customerActions/ActionsTab";
import AnnouncementsTab from "./customerActions/AnnouncementsTab";
import EmailsTab from "./customerActions/EmailsTab";
import FeedTab from "./customerActions/FeedTab";
import JourneysTab from "./customerActions/JourneysTab";
import { InspectModal } from "./customerActions/ui";
import { TABS, TIME_RANGES, parseRange, parseTab } from "./customerActions/helpers";

// Customer Actions: what logged-in Hub users do, one question per tab. The active tab,
// environment and range live in the URL (?tab=&env=&range=) so views can be shared; each
// tab fetches only what it needs (customerActions/*Tab.js over customerActions/helpers.js).

function CustomerActions() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get("tab"));
  const timeRange = parseRange(searchParams.get("range"));
  const useProd = searchParams.get("env") !== "staging";
  const update = useCallback(
    (patch) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          Object.entries(patch).forEach(([k, v]) => (v == null || v === "" ? next.delete(k) : next.set(k, v)));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [inspectedEvent, setInspectedEvent] = useState(null);
  const { data: overviewData = [] } = useGetCustomerOverviewQuery();
  const websites = useMemo(() => (Array.isArray(overviewData) ? overviewData : []), [overviewData]);
  const websiteNames = useMemo(() => {
    const m = {};
    websites.forEach((w) => {
      if (w && w.website_id) m[w.website_id] = w.name || w.website_name || w.website_id;
    });
    return m;
  }, [websites]);

  const active = TABS.find((t) => t.id === tab) || TABS[0];
  const rangeApplies = true;
  const pill = (on) => `px-3 py-1 text-body-xs font-semibold rounded-lg transition-all ${on ? "bg-accent-purple/10 text-accent-purple" : "text-text-tertiary hover:text-text-secondary"}`;

  return (
    <div className='space-y-6'>
      <div className='border-b border-border-subtle pb-4'>
        <div className='text-[11px] font-semibold text-text-tertiary uppercase tracking-wider'>
          Insights <span className='mx-1 text-gray-400'>/</span> <strong className='text-text-primary'>Customer Actions</strong>
        </div>
        <h1 className='text-h2 text-text-primary mt-1'>Customer Actions · what users do</h1>
        <p className='text-body-sm text-text-secondary mt-1'>
          Live Hub telemetry, one question per tab. Every number is computed from captured events; staff sessions are excluded.
        </p>
      </div>

      {/* Sticky tab bar with the controls that apply to the active tab */}
      <div className='sticky top-0 z-30 -mx-1 px-1 py-2 bg-surface-page/95 backdrop-blur-xs border-b border-border-subtle'>
        <div className='flex flex-wrap items-center justify-between gap-3'>
          <nav className='flex flex-wrap gap-1' aria-label='Customer Actions sections'>
            {TABS.map((t) => (
              <button
                key={t.id}
                type='button'
                onClick={() => update({ tab: t.id === TABS[0].id ? null : t.id })}
                aria-current={tab === t.id ? "page" : undefined}
                title={t.hint}
                className={`px-3 py-1.5 text-body-xs font-semibold rounded-xl transition-all ${
                  tab === t.id ? "bg-accent-purple text-white shadow-xs" : "bg-white border border-border-default text-text-secondary hover:bg-surface-neutral"
                }`}>
                {t.label}
              </button>
            ))}
          </nav>
          <div className='flex flex-wrap items-center gap-3'>
            <div className='flex bg-white rounded-xl border border-border-default p-1 shadow-xs' role='group' aria-label='Environment'>
              <button type='button' onClick={() => update({ env: null })} className={pill(useProd)}>Production</button>
              <button type='button' onClick={() => update({ env: "staging" })} className={pill(!useProd)}>Staging</button>
            </div>
            <div className={`flex bg-white rounded-xl border border-border-default p-1 shadow-xs ${rangeApplies ? "" : "opacity-40 pointer-events-none"}`} role='group' aria-label='Time range' title="Applies to every panel on this tab">
              {TIME_RANGES.map((r) => (
                <button key={r} type='button' onClick={() => update({ range: r === "30d" ? null : r })} className={pill(timeRange === r)}>
                  {r}
                </button>
              ))}
            </div>
          </div>
        </div>
        <p className='text-caption text-text-tertiary mt-1.5'>{active.label} · {active.hint} · {useProd ? "production" : "staging"} · last {timeRange}</p>
      </div>

      {tab === "actions" && <ActionsTab timeRange={timeRange} useProd={useProd} />}
      {tab === "emails" && <EmailsTab timeRange={timeRange} useProd={useProd} websiteNames={websiteNames} onInspect={setInspectedEvent} />}
      {tab === "announcements" && <AnnouncementsTab timeRange={timeRange} useProd={useProd} />}
      {tab === "feed" && <FeedTab timeRange={timeRange} useProd={useProd} websites={websites} onInspect={setInspectedEvent} />}
      {tab === "journeys" && <JourneysTab timeRange={timeRange} useProd={useProd} />}

      <InspectModal event={inspectedEvent} onClose={() => setInspectedEvent(null)} />
    </div>
  );
}

export default CustomerActions;
